import { useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { LoadingSpinner } from '@medtronic/pecc-react-component-library-js';
import { Box, Grid } from '@mui/material';
import { Dataset, ProjectTypeEnum } from '~/api/types';
import { useGetDatasetsForSelectionQuery, useGetUserDatasetsQuery } from '~/api/v2/hooks';
import { InfoFormLabel, MultipleSelect } from '~/components';
import { useAppSelector } from '~/redux';
import { selectCurrentUser } from '~/redux/slices/currentUserSlice';

const DatasetSelect = () => {
  const projectId = +useParams().projectid!;

  const { watch, unregister } = useFormContext();

  const projectType = watch('type') as ProjectTypeEnum;
  const isConsumerProject = projectType === ProjectTypeEnum.CONSUMER;

  const { data: sourceDatasets, isLoading: isSourceDatasetsLoading } = useGetDatasetsForSelectionQuery(
    { projectId },
    { skip: !projectId },
  );

  const { data: destinationDatasets, isLoading: isDestinationDatasetsLoading } = useGetDatasetsForSelectionQuery(
    { projectId, destinationType: true },
    { skip: !projectId || isConsumerProject },
  );

  const user = useAppSelector(selectCurrentUser);
  const userId = user?.id || '';

  const { data: userSourceDatasets } = useGetUserDatasetsQuery({ userId: userId }, { skip: !userId });
  const { data: userDestinationDatasets } = useGetUserDatasetsQuery(
    { userId: userId, destinationType: true },
    { skip: !userId },
  );

  useEffect(() => {
    if (!projectId) {
      unregister('dataset_ids');
      unregister('destination_dataset_ids');
    }
  }, [projectType]);

  const getSelectOptions = useCallback((datasetsList?: Dataset[]) => {
    return (
      datasetsList?.map(({ name, id }) => ({
        value: id,
        label: name,
      })) || []
    );
  }, []);

  const sourceDatasetOptions = useMemo(
    () => getSelectOptions(projectId ? sourceDatasets : userSourceDatasets || []),
    [sourceDatasets, userSourceDatasets],
  );

  const destinationDatasetsOptions = useMemo(
    () => getSelectOptions(projectId ? destinationDatasets : userDestinationDatasets || []),
    [destinationDatasets, userDestinationDatasets],
  );

  if (isSourceDatasetsLoading || (!isConsumerProject && isDestinationDatasetsLoading)) {
    return (
      <Box mt="1rem">
        <LoadingSpinner spinnerSize="s" />
      </Box>
    );
  }

  if (projectType === ProjectTypeEnum.CONSUMER) {
    return (
      <MultipleSelect
        name="dataset_ids"
        label="DATASETS"
        options={sourceDatasetOptions}
        placeholder="Select datasets"
      />
    );
  }

  return (
    <>
      <Grid size={6}>
        <MultipleSelect
          name="dataset_ids"
          label={
            <InfoFormLabel
              label="SOURCE DATASETS"
              tooltipText="Select the dataset where your original data resides. You need access permissions to use this dataset for extraction or processing."
            />
          }
          options={sourceDatasetOptions}
          placeholder="Select source datasets"
        />
      </Grid>
      <Grid size={6}>
        <MultipleSelect
          required
          name="destination_dataset_ids"
          label={
            <InfoFormLabel
              label="DESTINATION DATASETS"
              tooltipText="Select the dataset where processed or transformed data will be saved. Ensure you have write permissions for this destination."
            />
          }
          options={destinationDatasetsOptions}
          placeholder="Select destination datasets"
        />
      </Grid>
    </>
  );
};

export default DatasetSelect;
